/**
 * Frontend shapes of the user-impersonation flow (BR-AUTH-02/03). Mirrors
 * `apps/niko-backend-api/src/app/impersonation/dtos/impersonation-response.dto.ts` —
 * the backend DTO is the contract, this file only types what the UI reads.
 */

/** `localStorage` keys. Kept apart from `niko_tenant_session` on purpose: exiting an impersonation must leave the actor's own scoped token intact. */
export const IMPERSONATION_STORAGE_KEY = {
  SESSION: 'niko_impersonation_session',
  META: 'niko_impersonation_meta',
} as const;

/** `impersonation` block of `GET /users/me` and `GET /auth/me`; `null` outside impersonation. */
export interface ImpersonationMeta {
  sessionId: string;
  actorName: string;
  /** ISO timestamp — the banner counts down to it. */
  expiresAt: string;
  impersonatedUser: { id: string; name: string; lastname: string };
}

export interface StartImpersonationBody {
  tenantId: string;
  userId: string;
  /** 10–500 chars, mandatory. */
  reason: string;
}

export interface StartImpersonationResponse {
  accessToken: string;
  expiresAt: string;
  tenant: { id: string; name: string };
  user: { id: string; name: string; lastname: string; email: string };
}

/** Row of `GET /auth/impersonate/candidates` — identity fields only. */
export interface ImpersonationCandidate {
  id: string;
  name: string;
  lastname: string;
  email: string;
}

/** Row of `GET /auth/impersonate/sessions` — mirrors `ImpersonationSessionDto` (backend). */
export interface ImpersonationSession {
  id: string;
  tenantId: string;
  actorCognitoSub: string;
  actorUserId: string | null;
  actorName: string;
  impersonatedUserId: string;
  impersonatedUserName: string;
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  revokedAt: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  /** Derived by the backend: not ended, not revoked, not past `expiresAt`. */
  active: boolean;
}

/** Query of `GET /auth/impersonate/sessions`. `active: true` keeps only live sessions; omitted → all. */
export interface ImpersonationSessionsQuery {
  tenantId?: string;
  active?: boolean;
  page: number;
  limit: number;
}

/** Display state of a session row; derived on the client from the DTO dates (no extra backend field). */
export type ImpersonationSessionStatus = 'active' | 'ended' | 'revoked' | 'expired';

export function impersonationSessionStatus(
  s: Pick<ImpersonationSession, 'active' | 'revokedAt' | 'endedAt'>,
): ImpersonationSessionStatus {
  if (s.active) return 'active';
  if (s.revokedAt) return 'revoked';
  if (s.endedAt) return 'ended';
  return 'expired';
}
